import { createUseGesture, dragAction, wheelAction } from '@use-gesture/react';
import { getNodesBounds, useReactFlow, useStore } from '@xyflow/react';
import classnames from 'classnames';
import { memo, useCallback, useRef } from 'react';
import MiniMapNode from './MiniMapNode';

const getBoundsOfBoxes = (box1, box2) => ({
  x: Math.min(box1?.x, box2?.x),
  y: Math.min(box1?.y, box2?.y),
  x2: Math.max(box1?.x2, box2?.x2),
  y2: Math.max(box1?.y2, box2?.y2),
});

export const rectToBox = ({ x, y, width, height }) => ({
  x,
  y,
  x2: x + width,
  y2: y + height,
});

export const boxToRect = ({ x, y, x2, y2 }) => ({
  x,
  y,
  width: x2 - x,
  height: y2 - y,
});

export const getBoundsofRects = (rect1, rect2) =>
  boxToRect(getBoundsOfBoxes(rectToBox(rect1), rectToBox(rect2)));

const defaultWidth = 200;
const defaultHeight = 150;

const useGesture = createUseGesture([dragAction, wheelAction]);

const FlowMiniMap = ({
  style,
  className,
  nodeStrokeColor = '#555',
  nodeColor = '#fff',
  nodeClassName = '',
  nodeBorderRadius = 5,
  nodeStrokeWidth = 2,
  maskColor = 'rgb(240, 242, 243, 0.7)',
}) => {
  const containerWidth = useStore(s => s.width);
  const containerHeight = useStore(s => s.height);
  const [tX, tY, tScale] = useStore(s => s.transform);
  const nodes =
    useStore(s => {
      return Array.from(s?.nodes.entries()).map(a => a[1]);
    }) || [];
  const mapClasses = classnames(['react-flow__minimap', className]);
  const elementWidth = style?.width || defaultWidth;
  const elementHeight = style?.height || defaultHeight;
  const nodeColorFunc =
    nodeColor instanceof Function ? nodeColor : () => nodeColor;
  const nodeStrokeColorFunc =
    nodeStrokeColor instanceof Function
      ? nodeStrokeColor
      : () => nodeStrokeColor;
  const nodeClassNameFunc =
    nodeClassName instanceof Function ? nodeClassName : () => nodeClassName;
  const hasNodes = nodes && nodes.length;
  const bb = getNodesBounds(nodes);
  const viewBB = {
    x: -tX / tScale,
    y: -tY / tScale,
    width: containerWidth / tScale,
    height: containerHeight / tScale,
  };
  const boundingRect = hasNodes ? getBoundsofRects(bb, viewBB) : viewBB;
  const scaledWidth = boundingRect.width / elementWidth;
  const scaledHeight = boundingRect.height / elementHeight;
  const viewScale = Math.max(scaledWidth, scaledHeight);
  const viewWidth = viewScale * elementWidth;
  const viewHeight = viewScale * elementHeight;
  const offset = 5 * viewScale;
  const x = boundingRect.x - (viewWidth - boundingRect.width) / 2 - offset;
  const y = boundingRect.y - (viewHeight - boundingRect.height) / 2 - offset;
  const width = viewWidth + offset * 2;
  const height = viewHeight + offset * 2;
  const shapeRendering =
    typeof window === 'undefined' || !!window.chrome
      ? 'crispEdges'
      : 'geometricPrecision';

  // const minZoom = useStore(s => s.minZoom);
  // const maxZoom = useStore(s => s.maxZoom);

  const rectRef = useRef(null);

  const { setViewport } = useReactFlow();

  const onDrag = useCallback(
    ({ delta: [deltaX, deltaY] }) => {
      setViewport({
        x: tX + -deltaX * tScale * scaledWidth,
        y: tY + -deltaY * tScale * scaledHeight,
        zoom: tScale,
      });
    },
    [scaledHeight, scaledWidth, tScale, tX, tY, setViewport],
  );

  // const onWheel = useCallback(
  //   ({ event: { clientX, clientY, deltaY }, active }) => {
  //     if (!active) return;
  //     const sign = Math.sign(deltaY);

  //     const { clientRectX, clientRectY } =
  //       rectRef.current?.getBoundingClientRect() || {
  //         x: clientX,
  //         y: clientY,
  //       };
  //     const pX = clientX - clientRectX;
  //     const pY = clientY - clientRectY;

  //     const multiplier = sign === -1 ? 1.2 : sign === 1 ? 1 / 1.2 : 1;
  //     const zoom = tScale * multiplier;
  //     if (zoom > maxZoom || zoom < minZoom) return;
  //     setViewport({
  //       x: tX + sign * pX * zoom,
  //       y: tY + sign * pY * zoom,
  //       zoom,
  //     });
  //   },
  //   [tScale, maxZoom, minZoom, setViewport, tX, tY],
  // );

  const bind = useGesture({
    // onWheel,
    onDrag,
  });

  return (
    <svg
      width={elementWidth}
      height={elementHeight}
      viewBox={`${x} ${y} ${width} ${height}`}
      style={{ ...style, position: 'absolute', zIndex: 10 }}
      className={mapClasses}
    >
      {nodes
        .filter(node => !node.isHidden)
        .map(node => (
          <MiniMapNode
            key={node.id}
            x={node?.position?.x}
            y={node?.position?.y}
            width={node?.width}
            height={node?.height}
            style={node.style}
            className={nodeClassNameFunc(node)}
            color={nodeColorFunc(node)}
            borderRadius={nodeBorderRadius}
            strokeColor={nodeStrokeColorFunc(node)}
            strokeWidth={nodeStrokeWidth}
            shapeRendering={shapeRendering}
          />
        ))}
      <rect
        ref={rectRef}
        className="react-flow__minimap-mask"
        x={viewBB.x}
        y={viewBB.y}
        width={viewBB.width}
        height={viewBB.height}
        fill={maskColor}
        fillRule="evenodd"
        style={{
          touchAction: 'none',
        }}
        {...bind()}
      />
    </svg>
  );
};

FlowMiniMap.displayName = 'FlowMiniMap';

export default memo(FlowMiniMap);
